import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import SalonService from "../../services/SalonService";
import moment from "moment";
import "./ViewBookings.css";
import { toast } from "react-toastify";
import { useSalon } from "../../context/SalonContext";
import GlobalLoader from "../loadingIndicator/GlobalLoader";
import "../../style.css";
import {
    FaCalendarAlt, FaClock, FaCut, FaCheckCircle,
    FaTimesCircle, FaHourglassHalf, FaChevronRight,
    FaExclamationCircle, FaFilter,
    FaChevronDown, FaChevronUp, FaMapMarkerAlt, FaLock
} from "react-icons/fa";

/* -------------------------
   Helpers (Matching Mobile)
-------------------------- */
const getInitials = (name = "") => {
    const parts = (name || "").trim().split(/\s+/).slice(0, 2);
    return parts.map(p => p[0]?.toUpperCase() ?? "").join("");
};

const hashString = (str) => {
    let h = 0;
    for (let i = 0; i < str.length; i++) h = (h << 5) - h + str.charCodeAt(i);
    return Math.abs(h);
};

const PALETTES = [
    ["#6366F1", "#4F46E5"], // indigo
    ["#10B981", "#059669"], // emerald
    ["#3B82F6", "#2563EB"], // blue
    ["#EC4899", "#DB2777"], // pink
    ["#8B5CF6", "#7C3AED"], // violet
    ["#F59E0B", "#D97706"], // amber
    ["#14B8A6", "#0D9488"], // teal
];

const paletteFor = (key) => {
    const idx = hashString(key || "x") % PALETTES.length;
    return PALETTES[idx];
};

const ViewBookings = () => {
    const { customerId, salonName } = useSalon();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [dataSource, setDataSource] = useState([]);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const today = moment().format('YYYY-MM-DD');

    // Filter states
    const [filterExpanded, setFilterExpanded] = useState(false);
    const [year, setYear] = useState(new Date().getFullYear());
    const [segment, setSegment] = useState("all");
    const [status, setStatus] = useState(null);

    const [cancellationReason, setCancellationReason] = useState("");
    const [showCancelId, setShowCancelId] = useState(null);

    const STATUSES = ["BOOKED", "COMPLETED", "CANCELLED", "EXPIRED"];
    const SEGMENTS = [
        { id: "upcoming", label: "Upcoming" },
        { id: "past", label: "Past" },
        { id: "all", label: "All" },
    ];

    const years = useMemo(() => {
        const y = moment().year();
        return [y, y - 1, y - 2];
    }, []);

    const fetchAppointments = useCallback(async (page = 0) => {
        setLoading(true);
        try {
            const response = await SalonService.getUserAppointments(customerId, year, page, segment, status);
            const content = response?.data?.content || [];

            if (page === 0) {
                setDataSource(content);
            } else {
                setDataSource(prev => [...prev, ...content]);
            }

            // If we got fewer items than requested, assume no more
            if (content.length === 0 || content.length < 10) {
                setHasMore(false);
            } else {
                setHasMore(true);
            }
        } catch (error) {
            console.error("Error fetching appointments:", error);
            toast.error("Failed to load appointments");
        } finally {
            setLoading(false);
        }
    }, [customerId, year, segment, status]);

    useEffect(() => {
        setOffset(0);
        fetchAppointments(0);
    }, [segment, status, year, fetchAppointments]);

    const loadMore = () => {
        if (loading || !hasMore) return;
        const nextPage = offset + 1;
        setOffset(nextPage);
        fetchAppointments(nextPage);
    };

    const cancellationAllowed = (item) => {
        const currentDate = new Date();
        const timeInMinutes = (currentDate.getHours() * 60) + currentDate.getMinutes();
        const isFutureDate = item.apntDate > today;
        const isTodayWithGap = item.apntDate === today && timeInMinutes < item.apntStart - 60;

        return item.apntStatus === 'BOOKED' && (isFutureDate || isTodayWithGap);
    };

    const cancelAppointment = async (apntId) => {
        if (!cancellationReason.trim()) {
            toast.warn("Please provide a reason for cancellation");
            return;
        }

        setLoading(true);
        try {
            const response = await SalonService.cancelAppointment({ apntId, reason: cancellationReason });
            if (response.data) {
                toast.success('Appointment cancelled successfully');
                setDataSource(prev => prev.map(item =>
                    item.apntId === apntId ? { ...item, apntStatus: 'CANCELLED' } : item
                ));
                setShowCancelId(null);
                setCancellationReason("");
            } else {
                toast.error('Cancellation failed. Please contact salon.');
            }
        } catch (error) {
            toast.error('Cancellation failed. Please contact salon.');
        } finally {
            setLoading(false);
        }
    };

    /* -------------------------
       Sub-components
    -------------------------- */

    const PlaceholderAvatar = ({ name = "Salon", keySeed }) => {
        const initials = getInitials(name) || "S";
        const colors = paletteFor(keySeed || name);
        return (
            <div
                className="card-avatar-placeholder"
                style={{ background: `linear-gradient(135deg, ${colors[0]}, ${colors[1]})` }}
            >
                {initials}
            </div>
        );
    };

    const StatusBadge = ({ status }) => {
        const s = status?.toUpperCase();
        let config = { label: s, color: '#6B7280', icon: <FaClock /> };

        if (s === 'BOOKED') config = { label: "Booked", color: '#3B82F6', icon: <FaCalendarAlt /> };
        else if (s === 'COMPLETED') config = { label: "Completed", color: '#10B981', icon: <FaCheckCircle /> };
        else if (s === 'CANCELLED') config = { label: "Cancelled", color: '#EF4444', icon: <FaTimesCircle /> };
        else if (s === 'EXPIRED') config = { label: "Expired", color: '#9CA3AF', icon: <FaHourglassHalf /> };

        return (
            <div className="card-status-badge" style={{ backgroundColor: config.color + '15', color: config.color }}>
                <span className="badge-icon">{config.icon}</span>
                <span className="badge-text">{config.label}</span>
            </div>
        );
    };

    const PaymentBadge = ({ item }) => {
        if (item.apntStatus !== "BOOKED") return null;

        if (item.paymentStatus === "PENDING") {
            return (
                <div
                    className="card-payment-action"
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/pay/${item.bookingRef}`, {
                            state: {
                                customerId,
                                salonUuid: item.salonUuid,
                                bookingRef: item.bookingRef,
                                amount: item.advanceAmount,
                            },
                        });
                    }}
                >
                    <FaExclamationCircle size={10} />
                    <span>Pay Advance ₹{item.advanceAmount}</span>
                </div>
            );
        }

        if (item.paymentStatus === "PAID" || item.paymentStatus === "VERIFIED") {
            return (
                <div className="card-payment-success">
                    <FaCheckCircle size={10} />
                    <span>Paid</span>
                </div>
            );
        }

        return null;
    };

    return (
        <div className="view-bookings-page">
            <header className="loyalty-header-hero">
                <div className="content-wrapper">
                    <h1 className="header-title">My Appointments</h1>
                    <div className="mobile-accent-diamond">♦</div>
                    {salonName && <p className="salon-subtitle">Manage your style journey</p>}
                </div>
            </header>

            <div className="appointments-content-container">
                {/* Filter Section (Matching Mobile App) */}
                <div className="filter-section-premium">
                    <div
                        className="filter-toggle-btn"
                        onClick={() => setFilterExpanded(!filterExpanded)}
                    >
                        <div className="toggle-left">
                            <FaFilter className="me-2" />
                            <span>Filter Appointments</span>
                        </div>
                        {filterExpanded ? <FaChevronUp /> : <FaChevronDown />}
                    </div>

                    {filterExpanded && (
                        <div className="expanded-filters-box shadow-lg">
                            {/* Segment Control */}
                            <div className="segment-control-wrapper">
                                <div className="segment-control">
                                    {SEGMENTS.map((s) => (
                                        <button
                                            key={s.id}
                                            className={`segment-btn ${segment === s.id ? 'active' : ''}`}
                                            onClick={() => setSegment(s.id)}
                                        >
                                            {s.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Year Selector */}
                            <div className="filter-group">
                                <label className="filter-label">Year</label>
                                <div className="chip-container">
                                    {years.map((y) => (
                                        <span
                                            key={y}
                                            className={`filter-chip ${year === y ? 'active' : ''}`}
                                            onClick={() => setYear(y)}
                                        >
                                            {y}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Status Selector */}
                            <div className="filter-group mt-3">
                                <label className="filter-label">Status</label>
                                <div className="chip-container scrollable-chips">
                                    <span
                                        className={`filter-chip ${status === null ? 'active' : ''}`}
                                        onClick={() => setStatus(null)}
                                    >
                                        Any
                                    </span>
                                    {STATUSES.map((s) => (
                                        <span
                                            key={s}
                                            className={`filter-chip ${status === s ? 'active' : ''}`}
                                            onClick={() => setStatus(s)}
                                        >
                                            {s}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {loading && dataSource.length === 0 && <GlobalLoader />}

                {!loading && dataSource.length === 0 && (
                    <div className="empty-state-bookings">
                        <div className="empty-icon">📅</div>
                        <h3>No Appointments Found</h3>
                        <p>Try adjusting your filters to see more.</p>
                        <button className="btn btn-primary rounded-pill px-4 mt-3" onClick={() => {
                            setSegment("all");
                            setStatus(null);
                            setYear(new Date().getFullYear());
                        }}>
                            Reset Filters
                        </button>
                    </div>
                )}

                <div className="bookings-list">
                    {dataSource.map((item) => (
                        <div
                            key={item.apntId}
                            className={`premium-booking-card-v2 ${moment(item.apntDate).isBefore(today) ? 'past' : ''}`}
                            onClick={() => navigate(`/appointment/${item.apntId}`, {
                                state: {
                                    apntId: item.apntId,
                                    salonUuid: item.salonUuid
                                }
                            })}
                        >
                            <div className="card-v2-content">
                                {/* Left Side: Image / Avatar */}
                                <div className="card-v2-left">
                                    {item.image ? (
                                        <img
                                            src={`data:image/jpeg;base64,${item.image}`}
                                            className="card-v2-image"
                                            alt={item.salonName}
                                        />
                                    ) : (
                                        <PlaceholderAvatar name={item.salonName} keySeed={item.salonUuid} />
                                    )}
                                    <StatusBadge status={item.apntStatus} />
                                </div>

                                {/* Right Side: Info */}
                                <div className="card-v2-right">
                                    <div className="card-v2-header">
                                        <h3 className="card-v2-title">{item.salonName}</h3>
                                        <PaymentBadge item={item} />
                                    </div>

                                    {item.branchName && (
                                        <div className="card-v2-subtitle">
                                            <FaMapMarkerAlt size={10} className="me-1" />
                                            {item.branchName}
                                        </div>
                                    )}

                                    <div className="card-v2-grid">
                                        <div className="grid-item">
                                            <FaCalendarAlt size={12} />
                                            <span>{moment(item.apntDate).format("DD MMM, YYYY")}</span>
                                        </div>
                                        <div className="grid-item">
                                            <FaClock size={12} />
                                            <span>{item.apntTime}</span>
                                        </div>
                                    </div>

                                    <div className="card-v2-services">
                                        <FaCut size={12} />
                                        <span className="truncate-text">{item.serviceNames || "N/A"}</span>
                                    </div>

                                    <div className="card-v2-footer">
                                        <div className="otp-badge">
                                            <FaLock size={10} className="me-1" />
                                            <span className="label">OTP: </span>
                                            <span className="val">{item.verificationNumber || "----"}</span>
                                        </div>

                                        {cancellationAllowed(item) && !showCancelId && (
                                            <button
                                                className="cancel-small-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setShowCancelId(item.apntId);
                                                }}
                                            >
                                                Cancel
                                            </button>
                                        )}
                                        <FaChevronRight className="card-v2-arrow" />
                                    </div>

                                    {showCancelId === item.apntId && (
                                        <div className="card-v2-cancel-area" onClick={(e) => e.stopPropagation()}>
                                            <textarea
                                                placeholder="Enter reason..."
                                                value={cancellationReason}
                                                onChange={(e) => setCancellationReason(e.target.value)}
                                                className="cancel-textarea-v2"
                                            />
                                            <div className="cancel-actions-v2">
                                                <button className="btn-confirm-cancel" onClick={() => cancelAppointment(item.apntId)}>Confirm</button>
                                                <button className="btn-close-cancel" onClick={() => setShowCancelId(null)}>Close</button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {hasMore && (
                    <div className="load-more-section">
                        <button className="premium-load-more" onClick={loadMore} disabled={loading}>
                            {loading ? "Loading..." : "Load More Appointments"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ViewBookings;
